import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, forkJoin, Observable, of, pipe } from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';

import { NextCoachingService } from '../../coaching-base/coaching.service';
import { ObservationEntity } from '../../coaching-base/config';
import { DraftCommitmentService } from '../../coaching-base/draft-commitment.service';
import {
    BaseFocus,
    CoachingEventStatus,
    CoachingRole,
    CommitmentObservationLinkDto,
    CommitmentObservationLinkFocusPostDto,
    CommitmentObservationLinkPostDto,
    EventResult,
    FocusContainerDto,
    LookupBox,
    ObservationDto,
} from '../../coaching-base/general';
import { EventParticipantDto } from '../../coaching-base/general/event-participant-observation-dto.model';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { BehaviourItem } from '../../coaching-tags/models/behaviour.coaching.model';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { QueueItem } from '../../coaching-tags/models/queue.coaching.model';
import { SubBehaviourItem } from '../../coaching-tags/models/subbehaviour.coaching.model';
import { composeTagIdentifierNext } from '../../coaching-tags/tags/tags.model';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { ObservationCommitDialogItemStatus } from '../observation-commit-dialog/observation-commit-dialog.component';
import { focusContainerFormValueModelReverseTransformer } from '../observation-tag-group/tag-group.observation.form';

import { DraftIdByIdentifier } from './observation-focus-draft-id-identifier.model';
import {
    ActionType,
    RootDeleteAction,
    rootDeleteResult,
    RootReloadAction,
    rootReloadResult,
    RootSaveAction,
    rootSaveResult,
    RootSendNotificationAction,
    rootSendNotificationResult,
} from './observation.action';
import { composeObservation, sendNotification } from './observation.function';
import { NavigationScenario } from './observation.model';
import { ObservationService } from './observation.service';
import { RootState } from './observation.state';

export interface ChainGroup<TPayload> {
    root: TPayload;

    behaviours?: BehaviourItem[];
    subBehaviours?: SubBehaviourItem[];
    metrics?: MetricItem[];
    queues?: QueueItem[];

    coachingEvent?: ObservationEntity;

    event?: EventResult;
    performer?: EventParticipantDto;
    observer?: EventParticipantDto;
    focusContainers?: FocusContainerDto[];

    // TODO revise. see usages of state.dialogItems
    nextCommitmentDraftId?: string;
    draftIdByIdentifier?: DraftIdByIdentifier;
    nextCommitmentId?: number;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const rootReload = (
    action: ActionsObservable<PayloadAction<ActionType, RootReloadAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_OBSERVATION__ROOT__RELOAD),
        switchMap((act: PayloadAction<ActionType, RootReloadAction>) => {
            const coachingService =
                deps.injector.get<NextCoachingService>(NextCoachingService);
            const observationService =
                deps.injector.get<ObservationService>(ObservationService);
            const commitmentService =
                deps.injector.get<CommitmentService>(CommitmentService);
            const grp: ChainGroup<RootReloadAction> = { root: act.payload };

            const obses: [
                Observable<LookupBox>,
                Observable<ObservationEntity>,
                Observable<QueueItem[]>,
                Observable<ObservationDto>,
            ] = [
                of(null).pipe(
                    coachingService.retrieveLookupBox(),
                    tap((ret) => {
                        grp.behaviours = ret.behaviours;
                        grp.subBehaviours = ret.subBehaviours;
                        grp.metrics = ret.metrics;
                    }),
                ),
                of(null).pipe(
                    observationService.retrieveObservationConfig(), // TODO post-refactor. redux check if already loaded
                    // TODO consider consolidating
                    tap((ret) => {
                        // TODO revise vet and throw?
                        grp.coachingEvent = ret;
                    }),
                ),
                of(null).pipe(
                    commitmentService.listAvailableQueues(() => ({
                        userId: act.payload.performerId,
                    })),
                    // eslint-disable-next-line no-return-assign
                    tap((ret) => (grp.queues = ret)),
                ),
                of(null), // new or existing observation
            ];

            if (
                act.payload.observationId != null &&
                !isNaN(act.payload.observationId)
            ) {
                obses[3] = of(null).pipe(
                    observationService.retrieve(() => ({
                        observationId: act.payload.observationId,
                    })),
                );
            } else {
                obses[3] = of(null).pipe(
                    observationService.newDraft(() => ({
                        performerId: act.payload.performerId,
                        loadActive: act.payload.loadActive,
                        metricId: act.payload.activeMetricId,
                        isGeneric: act.payload.isGeneric,
                        draftCommitmentId: act.payload.draftCommitmentId,
                        commitmentId: act.payload.commitmentId,
                        focus: {
                            focusId: null,
                            userId: null,
                            behaviourId: act.payload.focus.behaviourId,
                            subBehaviourId: act.payload.focus.subBehaviourId,
                            createdBy: null,
                            createdOn: null,
                        },
                    })),
                );
            }

            return forkJoin(obses).pipe(
                // TODO move this tap() to reducer
                tap((ret) => {
                    grp.event = ret[3].event;

                    const participants = ret[3].eventParticipants;
                    grp.performer = participants.find(
                        (e) => e.coachingRoleId === CoachingRole.PERFORMER,
                    );
                    grp.observer = participants.find(
                        (e) => e.coachingRoleId === CoachingRole.OBSERVER,
                    );

                    grp.focusContainers = ret[3].focusContainers;
                }),
                map(() => {
                    return rootReloadResult(
                        {
                            ...act.payload,
                            behaviours: grp.behaviours,
                            subBehaviours: grp.subBehaviours,
                            metrics: grp.metrics,
                            coachingEvent: grp.coachingEvent,
                            event: grp.event,
                            performer: grp.performer,
                            observer: grp.observer,
                            focusContainers: grp.focusContainers,
                            queues: grp.queues,
                        },
                        act.storePath,
                    );
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootReloadResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSave = (
    action: ActionsObservable<PayloadAction<ActionType, RootSaveAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_OBSERVATION__ROOT__SAVE),
        switchMap((act: PayloadAction<ActionType, RootSaveAction>) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const service =
                deps.injector.get<ObservationService>(ObservationService);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );
            const grp: ChainGroup<RootSaveAction> = {
                root: act.payload,
                event: rootState.event,
                performer: rootState.performer,
                observer: rootState.observer,
                draftIdByIdentifier: {},
            };
            let obs = of(null);

            const value = act.payload.formValue;

            // only save actual changed observations, not placeholders created by the reducer
            // do not save focusContainers that have ONLY metrics inside and have no behaviourId
            /* let filteredFocusContainers = value.tagGroups.filter(a =>
                a.behaviourId
                || a.focusId
                || a.interactions.filter(i =>
                    i.identifier
                    || i.interactionId
                    || (i.notes && i.notes.filter(n => n.contents).length > 0)
                ).length > 0
            ) */
            const filteredFocusContainers = value.tagGroups.filter(
                (e) =>
                    e.trackingChanged ||
                    e.interactions.some((ee) => ee.interactionId != null),
            );

            const focusContainers = filteredFocusContainers.map(
                focusContainerFormValueModelReverseTransformer,
            );

            const observation = composeObservation(
                grp,
                value.deliveryType,
                value.startTime,
                value.duration,
                focusContainers,
                // interactions,
                // notes,
                act.payload.share,
                act.payload.publish,
            );

            if (observation.eventId === 0) {
                obs = obs.pipe(service.create(() => observation));
            } else {
                obs = obs.pipe(service.update(() => observation));
            }

            obs = obs.pipe(
                pipe(
                    tap((nextObservation) => {
                        grp.event = nextObservation.event;

                        const participants = nextObservation.eventParticipants;
                        grp.performer = participants.find(
                            (e) => e.coachingRoleId === CoachingRole.PERFORMER,
                        );
                        grp.observer = participants.find(
                            (e) => e.coachingRoleId === CoachingRole.OBSERVER,
                        );

                        grp.focusContainers = nextObservation.focusContainers;
                    }),
                    // grp.event is now === to nextObservation
                ),
                concatMap(() => {
                    let obsp = of(null);
                    if (!act.payload.publish || !act.payload.dialogItems) {
                        return obsp;
                    }

                    /* TODO move out from epic */
                    /* TODO move to api */
                    const commitToCommitments = (): Observable<any> => {
                        const nextFocus = composeTagIdentifierNext({
                            behaviourId: act.payload.primaryBehaviourId,
                            subBehaviourId: act.payload.primarySubBehaviourId,
                        });
                        const linkNavScenarios = [
                            NavigationScenario.NO_FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK,
                            NavigationScenario.FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK,
                        ];
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        const isLinking =
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            act.payload.actions &&
                            act.payload.actions.some((e) => {
                                return (
                                    e.type ===
                                        ActionType.COACHING_OBSERVATION__ROOT__NAVIGATE &&
                                    linkNavScenarios.includes(
                                        e.payload.scenario,
                                    )
                                );
                            });
                        const dialogItems = act.payload.dialogItems;
                        let focuses = dialogItems
                            .filter(
                                (e) =>
                                    e.Status ===
                                        ObservationCommitDialogItemStatus.UPDATED ||
                                    e.Status ===
                                        ObservationCommitDialogItemStatus.NEW,
                            )
                            .map((e) => {
                                const focus: BaseFocus<any> = {
                                    focusId: null,
                                    behaviourId: e.BehaviourId,
                                    subBehaviourId: e.SubBehaviourId,
                                    metrics: null,
                                };
                                const identifier =
                                    composeTagIdentifierNext(focus);
                                // pull from saved observation. ids would be set
                                const focusContainer = grp.focusContainers.find(
                                    (ee) =>
                                        composeTagIdentifierNext(ee) ===
                                        identifier,
                                );
                                if (!focusContainer) {
                                    // eslint-disable-next-line array-callback-return
                                    return;
                                }
                                if (nextFocus === identifier && isLinking) {
                                    // eslint-disable-next-line array-callback-return
                                    return;
                                }
                                const placeholderMetrics =
                                    focusContainer.metrics
                                        .filter((m) => !m.isActiveGoal)
                                        .map((metric) => metric.metricId);

                                const icnIds = focusContainer.interactions.map(
                                    (ee) => ee.interactionId,
                                );

                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                return {
                                    commitmentId: e.commitmentId,
                                    behaviourId: focus.behaviourId,
                                    subBehaviourId: focus.subBehaviourId,
                                    statusId:
                                        e.commitmentId == null
                                            ? CoachingEventStatus.DRAFT
                                            : CoachingEventStatus.ACTIVE,
                                    interactionIds: icnIds,
                                    placeholderMetrics,
                                } as CommitmentObservationLinkFocusPostDto;
                            });
                        focuses = focuses.filter((e) => e);

                        if (focuses.length === 0) {
                            return of(null);
                        }

                        return of(null).pipe(
                            draftCommitmentService.linkObservations(() => ({
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                dto: {
                                    performerId: grp.performer.userId,
                                    coachId: grp.observer.userId,
                                    focuses,
                                    timeZoneOffset:
                                        new Date().getTimezoneOffset(),
                                } as CommitmentObservationLinkPostDto,
                            })),
                            tap<CommitmentObservationLinkDto[]>((ret) => {
                                ret.forEach((e) => {
                                    const identifier =
                                        composeTagIdentifierNext(e);
                                    if (identifier === nextFocus) {
                                        grp.nextCommitmentDraftId = e.draftId;
                                        grp.nextCommitmentId = e.commitmentId;
                                    }

                                    grp.draftIdByIdentifier[identifier] =
                                        e.draftId;
                                });
                            }),
                            catchError((err) => {
                                console.log(err);
                                return of(null);
                            }),
                        );
                    };

                    if (act.payload.dialogItems) {
                        obsp = obsp.pipe(
                            concatMap(() => commitToCommitments()),
                        );
                    }

                    return obsp;
                }),
            );

            return obs.pipe(
                mergeMap(() => {
                    if (
                        act.payload.primaryBehaviourId &&
                        act.payload.primarySubBehaviourId
                    ) {
                        grp.focusContainers.forEach((x) => {
                            if (
                                act.payload.primaryBehaviourId ===
                                    x.behaviourId &&
                                act.payload.primarySubBehaviourId ===
                                    x.subBehaviourId
                            ) {
                                x.isPrimary = true;
                            }
                        });
                    }

                    const obses: Array<Observable<any>> = [
                        of(
                            rootSaveResult(
                                {
                                    formValue: grp.root.formValue,
                                    share: grp.root.share,
                                    publish: grp.root.publish,
                                    actions: grp.root.actions,
                                    event: grp.event,
                                    performer: grp.performer,
                                    observer: grp.observer,
                                    focusContainers: grp.focusContainers,
                                    nextCommitmentDraftId:
                                        grp.nextCommitmentDraftId,
                                    draftIdByIdentifier:
                                        grp.draftIdByIdentifier,
                                    nextCommitmentId: grp.nextCommitmentId,
                                },
                                act.storePath,
                            ),
                        ),
                    ];
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    act.payload.actions &&
                        act.payload.actions.forEach((e) => {
                            e.storePath = act.storePath;
                            // explicit checks
                            if (
                                e.type ===
                                ActionType.COACHING_OBSERVATION__ROOT__SEND_NOTIFICATION
                            ) {
                                obses.push(of(e));
                            }
                            if (
                                e.type ===
                                ActionType.COACHING_OBSERVATION__ROOT__NAVIGATE
                            ) {
                                obses.push(of(e));
                            }
                        });
                    return concat(...obses);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootSaveResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSendNotification = (
    action: ActionsObservable<
        PayloadAction<ActionType, RootSendNotificationAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_OBSERVATION__ROOT__SEND_NOTIFICATION),
        switchMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const grp: ChainGroup<RootSendNotificationAction> = {
                root: act.payload,
                event: rootState.event,
                performer: rootState.performer,
                observer: rootState.observer,
            };

            return of(null).pipe(
                sendNotification(deps, grp, act.payload),
                map(() =>
                    rootSendNotificationResult(
                        { ...act.payload },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootSendNotificationResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootDelete = (
    action: ActionsObservable<PayloadAction<ActionType, RootDeleteAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_OBSERVATION__ROOT__DELETE),
        switchMap((act) => {
            const service =
                deps.injector.get<ObservationService>(ObservationService);

            return of(null).pipe(
                service.delete(() => act.payload.observationId),
                mergeMap(() => {
                    const ret: Array<Observable<any>> = [
                        of(
                            rootDeleteResult(
                                { ...act.payload, success: true },
                                act.storePath,
                            ),
                        ),
                    ];
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    act.payload.actions &&
                        act.payload.actions.forEach((e) => {
                            e.storePath = act.storePath;
                            // explicit checks
                            if (
                                e.type ===
                                ActionType.COACHING_OBSERVATION__ROOT__NAVIGATE
                            ) {
                                ret.push(of(e));
                            }
                        });
                    return concat(...ret);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootDeleteResult(
                            { ...act.payload, success: false },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

export default [rootReload, rootSave, rootSendNotification, rootDelete];
